import React from 'react';
import { RenderNode } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES, Text } from '@contentful/rich-text-types';
import styled from 'styled-components';

import { ELEMENT_ACTION } from '../../shared/constants';

import TextLink from '../../components/atoms/text-link/TextLink';

import mq from '../../styles/breakpoints';
import Colors from '../../styles/colors';

import Media from '../../components/molecules/media/Media';
import { DEQ_TYPES } from '../deq';
import isInternalLink from '../generic/isInternalLink';

export const getDefaultOptions = (
  deqGroupTitle = '',
  deqGroupType = '',
  deqCompType = DEQ_TYPES.richTextHtml
): RenderNode => {
  return {
    [BLOCKS.PARAGRAPH]: (_node, children) => {
      if ((children as []).length >= 3) {
        // Videos and inline anchorlinks will be wrapped in a '<span>' element instead of a '<p>'
        return <span>{children}</span>;
      }

      return <p>{children}</p>;
    },
    [BLOCKS.QUOTE]: (_node, children) => {
      return <Blockquote>“{children}”</Blockquote>;
    },
    [INLINES.HYPERLINK]: ({ data, content }, children) => {
      let target = '';
      let customObject: {
        elementTarget: string;
        componentType: string;
        elementAction?: string;
      } = {
        componentType: deqCompType,
        elementTarget: `${data.uri}`,
      };
      let elementType = 'textlink';

      // LinkedIn player in hyperlink
      if (data.uri.includes('linkedin.com/video/embed')) {
        return <Media src={data.uri} type="LinkedIn-Video" />;
      }
      // YouTube player in hyperlink
      if (data.uri.includes('youtube.com/embed')) {
        return <Media src={data.uri} type="Youtube-Embed" />;
      }
      // Audiboom player in hyperlink
      if (data.uri.includes('embeds.audioboom.com')) {
        return <Media src={data.uri} type="Audioboom-Podcast" />;
      }
      // Internal link
      if (isInternalLink(data.uri)) {
        target = '_self';
        customObject = {
          componentType: deqCompType,
          elementTarget: `${data.uri}`,
        };
      }
      // Mail
      else if (data.uri.startsWith('mailto:')) {
        elementType = 'contact';
        target = '_blank';
        customObject = {
          componentType: deqCompType,
          elementTarget: `${data.uri}`,
          elementAction: ELEMENT_ACTION.mail,
        };
      }
      // Call
      else if (data.uri.startsWith('tel:')) {
        elementType = 'contact';
        target = '_blank';
        customObject = {
          componentType: deqCompType,
          elementTarget: `${data.uri}`,
          elementAction: ELEMENT_ACTION.call,
        };
      }
      // External link, needs to be last sinds mailto etc don't start with domain
      else if (
        !isInternalLink(data.uri) &&
        (!data.uri.includes('linkedin.com/video/embed') ||
          !data.uri.includes('youtube.com/embed') ||
          !data.uri.includes('embeds.audioboom.com'))
      ) {
        customObject = {
          componentType: deqCompType,
          elementTarget: `${data.uri}`,
          elementAction: ELEMENT_ACTION.exit,
        };

        target = '_blank';
      }
      // DownloadIcon -> assets.ctfassets (CDN Contentful)
      else if (data.uri.startsWith('//assets.ctfassets')) {
        customObject = {
          componentType: deqCompType,
          elementTarget: `${data.uri}`,
          elementAction: ELEMENT_ACTION.download,
        };

        target = '_blank';
      }

      // First child is this url is the text of the link, we are NOT using
      // children[0] as content of this custom object because this can contain
      // other elements link <p> or <img> tags that need there own custom renderer
      const deqLabel = (content[0] as Text)?.value ?? null;

      return (
        <TextLink
          href={data.uri}
          target={target}
          deqType={elementType}
          deqLabel={deqLabel}
          deqObject={customObject}
          deqGroupTitle={deqGroupTitle}
          deqGroupType={deqGroupType}
        >
          {children}
        </TextLink>
      );
    },
  };
};

const Blockquote = styled.blockquote`
  color: ${Colors.black};
  font-family: ${({ theme }) => theme.fontCondensed};
  font-size: ${({ theme }) => theme.blockquote.fontSize.S};
  line-height: ${({ theme }) => theme.blockquote.lineHeight.S};
  margin: 48px 0;
  width: 100%;

  > p {
    display: inline;
  }

  ${mq.from.breakpoint.M`
    margin: 64px 0;
    position: relative;
    font-size: ${({ theme }) => theme.blockquote.fontSize.L};
    line-height: ${({ theme }) => theme.blockquote.lineHeight.L};

    > span {
      position: absolute;
      left: -32px;
    }
  `}
`;
